import { useEffect, useState, type ReactNode } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router';
import { useCycles, useLogout } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { SpiritG, Wordmark } from '../components/Brand';
import { useNotify } from '../components/Notify';
import { AddProspectDialog } from '../drawer/AddProspectDialog';
import { HouseholdDrawer } from '../drawer/HouseholdDrawer';
import { BRAND } from '../theme';
import { useUrlState } from '../urlState';
import { visibleViews } from './views';

function initials(name: string) {
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .slice(0, 2)
    .join('');
}

export function AppShell({ children }: { children: ReactNode }) {
  const { me, can } = useAuth();
  const { notifyError } = useNotify();
  const location = useLocation();
  const navigate = useNavigate();
  const { params, set } = useUrlState();
  const logout = useLogout();
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const staff = can('households.read');

  const views = visibleViews(can);
  const current = views.find((v) => location.pathname.startsWith(v.path))?.path ?? false;
  const keep = params.get('cycle') ? `?cycle=${params.get('cycle')}` : '';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0} sx={{ background: `linear-gradient(90deg, ${BRAND.navy} 0%, #012a86 100%)` }}>
        <Toolbar sx={{ gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, py: { xs: 1, md: 0 } }}>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
            <SpiritG size={38} color="#fff" />
            <Box>
              <Stack component="div" direction="row" alignItems="center" gap={1} sx={{ color: '#fff' }}>
                <Wordmark height={19} inverse />
                <Typography variant="h1" component="span" sx={{ color: '#fff', fontWeight: 600, pl: 1, borderLeft: '1px solid rgba(255,255,255,.4)', lineHeight: 1.1 }}>
                  HV Platform
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: '#c6d0e6' }}>
                {staff ? 'High Visibility applicant pipeline' : 'Trustee referrals'}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          {staff && <StaffControls />}
          {can('households.write') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ bgcolor: '#fff', color: BRAND.navy, '&:hover': { bgcolor: '#eef2fb' } }}>
              Add Prospect
            </Button>
          )}
          <IconButton aria-label="Account menu" onClick={(e) => setMenuEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#fff', color: BRAND.navy, fontSize: 14, fontWeight: 700 }}>{initials(me.displayName || me.email)}</Avatar>
          </IconButton>
          <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
            <MenuItem disabled>
              <ListItemText primary={me.displayName} secondary={me.email} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                logout.mutate(undefined, { onError: (err) => notifyError(err, 'Sign out failed') });
              }}
            >
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {views.length > 0 && (
        <Box component="nav" aria-label="Views" sx={{ bgcolor: '#fff', borderBottom: `1px solid ${BRAND.line}`, px: 2 }}>
          <Tabs value={current} onChange={(_, v: string) => navigate(`${v}${keep}`)}>
            {views.map((v) => (
              <Tab key={v.path} value={v.path} label={v.label} onClick={() => v.path === current && location.pathname !== v.path && navigate(`${v.path}${keep}`)} />
            ))}
          </Tabs>
        </Box>
      )}
      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Box>
      {staff && <HouseholdDrawer />}
      {addOpen && (
        <AddProspectDialog
          onClose={() => setAddOpen(false)}
          onCreated={(id) => {
            setAddOpen(false);
            set({ h: id }, false);
          }}
        />
      )}
    </Box>
  );
}

function StaffControls() {
  const { notifyError } = useNotify();
  const location = useLocation();
  const navigate = useNavigate();
  const { params, set } = useUrlState();
  const cycles = useCycles();
  const [search, setSearch] = useState(params.get('q') ?? '');

  const urlQ = params.get('q') ?? '';
  useEffect(() => setSearch(urlQ), [urlQ]);
  useEffect(() => {
    if (cycles.error) notifyError(cycles.error, 'Loading admission cycles failed');
  }, [cycles.error, notifyError]);

  useEffect(() => {
    if (search === urlQ) return;
    const t = setTimeout(() => {
      if (location.pathname.startsWith('/pipeline')) set({ q: search || null });
      else if (search) navigate(`/pipeline?${new URLSearchParams({ ...(params.get('cycle') ? { cycle: params.get('cycle')! } : {}), q: search })}`);
    }, 300);
    return () => clearTimeout(t);
  }, [search, urlQ, location.pathname, set, navigate, params]);

  const activeCycle = cycles.data?.find((c) => c.active);
  const cycleValue = params.get('cycle') ?? activeCycle?.id ?? '';

  return (
    <>
      <TextField
        size="small"
        placeholder="Search family, applicant, recommender, narrative…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{
          htmlInput: { 'aria-label': 'Search households' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#c6d0e6' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          width: { xs: '100%', md: 360 },
          order: { xs: 3, md: 0 },
          '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } },
          '& input::placeholder': { color: '#c6d0e6', opacity: 1 },
        }}
      />
      <Select
        size="small"
        value={cycles.data ? cycleValue : ''}
        displayEmpty
        onChange={(e) => set({ cycle: e.target.value === activeCycle?.id ? null : e.target.value, h: null })}
        inputProps={{ 'aria-label': 'Admission cycle' }}
        sx={{ color: '#fff', minWidth: 120, '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' }, '& svg': { color: '#fff' } }}
      >
        {!cycles.data?.length && <MenuItem value="">No cycle</MenuItem>}
        {cycles.data?.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.code}
            {c.active ? ' (active)' : ''}
          </MenuItem>
        ))}
      </Select>
    </>
  );
}
